/**
 * Indicadores de pedido calculados sobre a fila UNIFICADA (todos os canais, inclusive os pedidos
 * reais do Bling), em vez de só sobre o checkout da Loja.
 *
 * Os cards "Faturamento", "Pedidos" e "Pedidos pendentes" de `buildDashboardKpis` nascem do
 * dataset do checkout (`/api/checkout/orders`) e seus drill-downs usam ids do checkout. Quando a
 * fonte unificada está publicada, estes valores a substituem: o número da diretoria passa a ser
 * o que está no Bling, e o drill-down aponta para `/pedidos` (lista completa, filtrável por canal).
 * Puro e sem I/O; sem a fonte unificada nenhum KPI é alterado (o dashboard continua declarando o
 * recorte do checkout).
 */
import type { Kpi } from "../dashboard/kpis.js";
import type { PlatformUnifiedOrder } from "../api/types.js";
import { openOrders, revenueSummary } from "./list-view.js";
import { isRevenueState } from "./state-machine.js";
import { salesChannelBuckets } from "./channels.js";

const brl = (cents: number): string => (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function applyUnifiedOrderKpis(kpis: Kpi[], orders: PlatformUnifiedOrder[] | undefined): Kpi[] {
  if (!orders) return kpis;
  const revenue = revenueSummary(orders);
  const open = openOrders(orders);
  const channels = salesChannelBuckets(orders);
  const channelsNote = channels.slice(0, 4).map((bucket) => `${bucket.label}: ${bucket.count}`).join(" · ");
  return kpis.map((kpi) => {
    if (kpi.id === "faturamento") {
      return {
        ...kpi,
        value: revenue.revenueCents,
        note: `Soma de ${revenue.revenueOrders} pedido(s) que valem venda em todos os canais: ${brl(revenue.revenueCents)}. Cancelados e devolvidos ficam de fora.`,
        drillDown: { route: "/pedidos", label: "Ver pedidos de todos os canais", recordKind: "order" as const, recordIds: orders.filter((order) => isRevenueState(order.state)).map((order) => order.id) }
      };
    }
    if (kpi.id === "pedidos") {
      return {
        ...kpi,
        value: orders.length,
        note: `${orders.length} pedido(s) de todos os canais${channelsNote ? ` (${channelsNote})` : ""}.`,
        drillDown: { route: "/pedidos", label: "Ver todos os pedidos", recordKind: "order" as const, recordIds: orders.map((order) => order.id) }
      };
    }
    if (kpi.id === "pedidos_pendentes") {
      return {
        ...kpi,
        value: open.length,
        note: open.length
          ? "Pedidos ainda não concluídos em nenhum canal: da cobrança ao faturamento/entrega no Bling."
          : "Nenhum pedido pendente.",
        drillDown: { route: "/pedidos", label: "Ver pedidos em aberto", recordKind: "order" as const, recordIds: open.map((order) => order.id) }
      };
    }
    return kpi;
  });
}
